// Bank-run rules, ported from the Library cash page's bank-run card
// (public/js/cash-management.js) so a run means the same thing in both apps.
//
// get_pending_bank_deposit returns only days whose every bag is verified and
// that no run has swept yet. verify_bank_run sweeps them (all, or only the
// p_dates given) into one deposit_bank_runs row. Station scopes a run to one
// month so August and September can be deposited and recorded separately.

import { parseDollarsToCents } from "./depositVerify";

export interface PendingBag {
  device_name: string | null;
  staff_counted_cents: number | null;
  counted_by: string | null;
  bag_number: string | null;
  wizard_deposit_cents: number | null;
}

export interface PendingDay {
  business_date: string; // YYYY-MM-DD
  actual_deposit_cents: number;
  bags: PendingBag[];
}

export const ALL_PENDING = "all";

/** "YYYY-MM" keys of the months that have pending days, oldest first. */
export function pendingMonths(days: PendingDay[]): string[] {
  return [...new Set(days.map((d) => d.business_date.slice(0, 7)))].sort();
}

export function daysInScope(days: PendingDay[], scope: string): PendingDay[] {
  const inScope = scope === ALL_PENDING ? days : days.filter((d) => d.business_date.slice(0, 7) === scope);
  return [...inScope].sort((a, b) => a.business_date.localeCompare(b.business_date));
}

export function scopeExpectedCents(days: PendingDay[]): number {
  return days.reduce((sum, d) => sum + (d.actual_deposit_cents || 0), 0);
}

/** p_dates for verify_bank_run: always the days shown, never null. A null sweep
 *  would also take a day another manager completed after this screen loaded, or
 *  one held back for an open drawer, and the count would not cover it. */
export function scopeDates(days: PendingDay[]): string[] {
  return days.map((d) => d.business_date);
}

export function monthLabel(key: string): string {
  if (key === ALL_PENDING) return "Everything pending";
  const [y, m] = key.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("en-US", { month: "long", year: "numeric" });
}

export interface RunCheck {
  ok: boolean;
  error?: string;
  countedCents: number | null;
  varianceCents: number | null;
}

/**
 * The guardrails on confirming a bank run (back-office parity):
 *  - the counted total must be a money amount;
 *  - $0 against real bags is a typo or an empty pile, refused outright;
 *  - any variance needs a note, because explaining it is the point of the run.
 */
export function checkBankRun(args: { expectedCents: number; countedInput: string; note: string; dayCount: number }): RunCheck {
  if (args.dayCount === 0) return { ok: false, error: "No verified days in this period.", countedCents: null, varianceCents: null };
  const counted = parseDollarsToCents(args.countedInput);
  if (counted == null) return { ok: false, error: "Enter the total counted for the bank.", countedCents: null, varianceCents: null };
  const variance = counted - args.expectedCents;
  if (counted === 0 && args.expectedCents > 0) {
    return { ok: false, error: "A $0 count against real bags looks like a typo. Re-enter the amount.", countedCents: counted, varianceCents: variance };
  }
  if (variance !== 0 && !args.note.trim()) {
    return { ok: false, error: "Add a note explaining the variance before confirming.", countedCents: counted, varianceCents: variance };
  }
  return { ok: true, countedCents: counted, varianceCents: variance };
}

export interface MonthDayStatus {
  business_date: string;
  total_drawers: number;
  drawers_closed: number;
  has_unverified_bags: boolean;
  banked: boolean;
}

export interface LeftOutDay {
  business_date: string;
  reason: "drawer-open" | "late-bag" | "unverified";
}

/**
 * Days a run must not include yet. verify_drawer_deposit completes a day once
 * every drawer CLOSED at that moment is verified, so a pending day can carry a
 * partial total in two ways, and both are held back (excludeHeldDays):
 *  - drawer-open: a drawer is still open in Square;
 *  - late-bag: a drawer closed after the day was completed, so the day has
 *    fewer verified bags than closed drawers. Verifying that bag recomputes
 *    the day's total, after which it can go in a run.
 * Days with bags not verified yet are listed but were never pending anyway.
 */
export function daysLeftOut(monthDays: MonthDayStatus[], pendingDays: PendingDay[] = []): LeftOutDay[] {
  const bagsByDay = new Map(pendingDays.map((p) => [p.business_date, p.bags.length]));
  const out: LeftOutDay[] = [];
  for (const d of monthDays) {
    if (d.banked) continue;
    const bags = bagsByDay.get(d.business_date);
    if (d.total_drawers > d.drawers_closed) out.push({ business_date: d.business_date, reason: "drawer-open" });
    else if (bags !== undefined && bags < d.drawers_closed) out.push({ business_date: d.business_date, reason: "late-bag" });
    else if (d.drawers_closed > 0 && d.has_unverified_bags) out.push({ business_date: d.business_date, reason: "unverified" });
  }
  return out;
}

export function excludeHeldDays(days: PendingDay[], leftOut: LeftOutDay[]): PendingDay[] {
  const held = new Set(leftOut.filter((d) => d.reason !== "unverified").map((d) => d.business_date));
  return days.filter((d) => !held.has(d.business_date));
}
